import { z } from 'zod';
import { FLEET_RECOVERY_DEVICE_DOMAIN, FLEET_RECOVERY_MAX_ITEMS, FLEET_RECOVERY_MAX_RECEIPT_BYTES,
  fleetRecoveryDeviceIdSchema, fleetRecoveryIdSchema, fleetRecoveryNonceSchema,
  fleetRecoveryPublicKeySchema, fleetRecoverySha256Schema, fleetRecoverySignatureSchema,
  fleetRecoveryUint64Schema, fleetRecoveryUtcSchema, fleetRecoveryUuidSchema,
  fleetRecoveryVersionSchema, fleetRecoveryPathSchema } from './fleetRecovery/scalars';
import { installationManifestSchema } from './fleetRecovery/manifests';
import { FleetRecoveryRebootManifest } from './fleetRecovery/FleetRecoveryRebootManifest';
import type { InstallationManifest } from './fleetRecoveryTypes';

export * from './fleetRecovery/scalars';
export { FleetRecoveryCanonicalJson, FleetRecoveryWireDecoder } from './fleetRecovery/FleetRecoveryCanonicalJson';
export { FleetRecoveryAdmissionPolicy, type FleetRecoveryAdmissionContext,
  type FleetRecoveryAdmissionReason } from './fleetRecovery/FleetRecoveryAdmissionPolicy';
export { recoveryArtifactSchema, readinessProbeSchema, installationManifestSchema } from './fleetRecovery/manifests';
export { targetConfigurationSchema, ownedTargetSchema, ownershipRuleSchema } from './fleetRecovery/targets';
export * from './fleetRecovery/authority';
export { FleetRecoveryFoundationSelection, type FleetRecoveryReleasePurpose } from './fleetRecovery/FleetRecoveryFoundationSelection';
export { FleetRecoveryOperationPolicy } from './fleetRecovery/FleetRecoveryOperationPolicy';
export { FleetRecoveryRebootManifest } from './fleetRecovery/FleetRecoveryRebootManifest';

export const FLEET_RECOVERY_PROTOCOL_VERSION = 2 as const;
export const FLEET_RECOVERY_EXECUTOR_PROTOCOL_VERSION = 4 as const;
export const FLEET_RECOVERY_DEFAULT_MAX_REBOOTS = 8;
export const FLEET_RECOVERY_DEFAULT_DEADLINE_MS = 24 * 60 * 60 * 1000;
export const FLEET_RECOVERY_HEALTH_MAX_AGE_MS = 120000;
export const FLEET_RECOVERY_CHALLENGE_TTL_MS = 60000;
export const FLEET_RECOVERY_CLOCK_SKEW_MS = 120000;

export const recoveryKindSchema = z.enum(['install', 'upgrade', 'repair', 'rebuild-product',
  'replace-updater', 'replace-rescue', 'rotate-authority', 'uninstall-product', 'decommission', 'force-upgrade', 'force-reboot']);
export const recoveryStageSchema = z.enum(['admitted', 'staged', 'quiescing', 'removing',
  'awaiting-reboot', 'verifying-clean', 'installing', 'verifying-ready', 'probation',
  'succeeded', 'failed', 'blocked-recoverable', 'cancelled', 'outcome-unknown']);
export const recoveryNextActionSchema = z.enum(['inspect', 'stage', 'quiesce', 'remove', 'reboot',
  'verify-clean', 'install', 'verify-ready', 'promote', 'restore', 'wait', 'none']);
export const recoveryIdentitySchema = z.object({
  schemaVersion: z.literal(2), deviceId: fleetRecoveryDeviceIdSchema, operationId: fleetRecoveryUuidSchema,
  generation: fleetRecoveryUint64Schema.refine(value => value !== '0', 'Operation generation starts at one'),
  manifestSha256: fleetRecoverySha256Schema, correlationId: fleetRecoveryUuidSchema,
}).strict();
export const recoveryOperationSchema = recoveryIdentitySchema.extend({
  kind: recoveryKindSchema, issuedAtUtc: fleetRecoveryUtcSchema, expiresAtUtc: fleetRecoveryUtcSchema,
  deadlineUtc: fleetRecoveryUtcSchema, maxReboots: z.number().int().min(1).max(32),
  packageSha256: fleetRecoverySha256Schema, minExecutorVersion: fleetRecoveryVersionSchema,
  authorizationId: fleetRecoveryIdSchema,
}).strict().superRefine((operation, context) => {
  if (['force-upgrade', 'force-reboot'].includes(operation.kind) && operation.minExecutorVersion < 4) {
    context.addIssue({ code: 'custom', path: ['minExecutorVersion'], message: 'Forced administration requires executor protocol four' });
  }
  if (operation.kind === 'rotate-authority' && operation.minExecutorVersion < 3) {
    context.addIssue({ code: 'custom', path: ['minExecutorVersion'], message: 'Authority rotation requires executor protocol three' });
  }
  if (Date.parse(operation.expiresAtUtc) <= Date.parse(operation.issuedAtUtc)) {
    context.addIssue({ code: 'custom', path: ['expiresAtUtc'], message: 'Admission expiry must follow issue time' });
  }
  if (Date.parse(operation.deadlineUtc) < Date.parse(operation.expiresAtUtc)) {
    context.addIssue({ code: 'custom', path: ['deadlineUtc'], message: 'Operation deadline must cover admission window' });
  }
});
export const recoveryEvidenceSchema = z.object({
  targetId: fleetRecoveryIdSchema, observation: z.enum(['present', 'absent', 'healthy', 'failed', 'unknown']),
  observedAtUtc: fleetRecoveryUtcSchema, bootId: fleetRecoveryIdSchema,
  osCode: z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER).nullable(),
  detailCode: fleetRecoveryIdSchema, evidenceSha256: fleetRecoverySha256Schema,
}).strict();
export const installationReceiptSchema = recoveryIdentitySchema.extend({
  attemptId: fleetRecoveryUuidSchema, bootId: fleetRecoveryIdSchema, stage: recoveryStageSchema,
  sequence: fleetRecoveryUint64Schema, observedAtUtc: fleetRecoveryUtcSchema,
  rebootCount: z.number().int().min(0).max(32), residualSha256: fleetRecoverySha256Schema,
  evidence: z.array(recoveryEvidenceSchema).max(FLEET_RECOVERY_MAX_ITEMS), nextAction: recoveryNextActionSchema,
}).strict().superRefine((receipt, context) => {
  if (new TextEncoder().encode(JSON.stringify(receipt)).length > FLEET_RECOVERY_MAX_RECEIPT_BYTES) {
    context.addIssue({ code: 'custom', message: 'Receipt exceeds one MiB' });
  }
  if (receipt.stage === 'succeeded' && (receipt.nextAction !== 'none' || receipt.evidence.length === 0
    || receipt.evidence.some(evidence => evidence.observation === 'unknown' || evidence.observation === 'failed'))) {
    context.addIssue({ code: 'custom', message: 'Success requires conclusive evidence and no remaining action' });
  }
});
export const foundationHealthSchema = z.object({
  schemaVersion: z.literal(2), deviceId: fleetRecoveryDeviceIdSchema, generation: fleetRecoveryUint64Schema,
  updaterSha256: fleetRecoverySha256Schema, rescueSha256: fleetRecoverySha256Schema,
  baselineSha256: fleetRecoverySha256Schema, bootId: fleetRecoveryIdSchema, observedAtUtc: fleetRecoveryUtcSchema,
  directPolling: z.boolean(), taskVerified: z.boolean(), cachedRestoreVerified: z.boolean(),
  state: z.enum(['ready', 'degraded', 'unknown']),
}).strict().superRefine((health, context) => {
  if (health.rescueSha256 === health.baselineSha256) context.addIssue({ code: 'custom', path: ['baselineSha256'], message: 'Rescue baseline must be independently built' });
  if (health.state === 'ready' && (!health.directPolling || !health.taskVerified || !health.cachedRestoreVerified)) {
    context.addIssue({ code: 'custom', path: ['state'], message: 'Ready requires independent polling, two task paths and cached restoration proof' });
  }
});

export const deviceProofSchema = z.object({
  domain: z.literal(FLEET_RECOVERY_DEVICE_DOMAIN), deviceId: fleetRecoveryDeviceIdSchema, keyId: fleetRecoveryIdSchema,
  challengeId: fleetRecoveryUuidSchema, nonce: fleetRecoveryNonceSchema, issuedAtUtc: fleetRecoveryUtcSchema,
  requestSha256: fleetRecoverySha256Schema, signature: fleetRecoverySignatureSchema,
}).strict();
export function recoveryEnvelopeSchema<T extends z.ZodTypeAny>(payloadSchema: T) {
  return z.object({ algorithm: z.literal('Ed25519'), payload: payloadSchema,
    keyId: fleetRecoveryIdSchema, signature: fleetRecoverySignatureSchema }).strict();
}
export const fleetRecoveryChallengeRequestSchema = z.object({
  deviceId: fleetRecoveryDeviceIdSchema, keyId: fleetRecoveryIdSchema, requestSha256: fleetRecoverySha256Schema,
}).strict();
export const fleetRecoveryChallengeSchema = z.object({
  challengeId: fleetRecoveryUuidSchema, nonce: fleetRecoveryNonceSchema, expiresAtUtc: fleetRecoveryUtcSchema,
}).strict();
export const fleetRecoveryEnrollmentSchema = z.object({
  deviceId: fleetRecoveryDeviceIdSchema, keyId: fleetRecoveryIdSchema, publicKey: fleetRecoveryPublicKeySchema,
  enrollmentGrant: z.string().min(1).max(16384), proof: deviceProofSchema,
}).strict().refine(input => input.proof.deviceId === input.deviceId && input.proof.keyId === input.keyId,
  'Enrollment proof must bind the enrolling device and key');
export const fleetRecoveryEnrollmentReceiptSchema = recoveryEnvelopeSchema(z.object({
  deviceId: fleetRecoveryDeviceIdSchema, keyId: fleetRecoveryIdSchema, acceptedAtUtc: fleetRecoveryUtcSchema,
}).strict());
export const fleetRecoveryPollSchema = z.object({
  proof: deviceProofSchema, generation: fleetRecoveryUint64Schema, executorVersion: fleetRecoveryVersionSchema,
  rescueVersion: fleetRecoveryVersionSchema, cursor: fleetRecoveryIdSchema.nullable(),
}).strict();
const pollPayloadSchema = z.object({
  deviceId: fleetRecoveryDeviceIdSchema, cursor: fleetRecoveryIdSchema,
  operation: recoveryOperationSchema.nullable(), manifest: installationManifestSchema.nullable(),
}).strict().superRefine((payload, context) => {
  if ((payload.operation === null) !== (payload.manifest === null)) context.addIssue({ code: 'custom', message: 'Operation and manifest must be delivered together' });
  if (payload.operation && payload.operation.deviceId !== payload.deviceId) context.addIssue({ code: 'custom', message: 'Operation targets different device' });
  if (payload.operation?.kind === 'force-reboot') {
    if (!payload.manifest || !FleetRecoveryRebootManifest.matches(payload.manifest as InstallationManifest)
      || payload.operation.packageSha256 !== FleetRecoveryRebootManifest.reference.packageSha256)
      context.addIssue({ code: 'custom', message: 'Reboot requires mutation-free control authority' });
    return;
  }
  if (payload.manifest?.productVersion === FleetRecoveryRebootManifest.version)
    context.addIssue({ code: 'custom', message: 'Control authority cannot install artifacts' });
  if (payload.operation && payload.manifest && !payload.manifest.artifacts.some(artifact => artifact.sha256 === payload.operation?.packageSha256)) {
    context.addIssue({ code: 'custom', message: 'Operation package is absent from exact manifest' });
  }
});
export const fleetRecoveryPollReplySchema = recoveryEnvelopeSchema(pollPayloadSchema);
export const fleetRecoveryReceiptRequestSchema = z.object({ proof: deviceProofSchema, receipt: installationReceiptSchema }).strict()
  .refine(input => input.proof.deviceId === input.receipt.deviceId, 'Receipt proof device mismatch');
export const fleetRecoveryReceiptAckSchema = z.object({
  operationId: fleetRecoveryUuidSchema, sequence: fleetRecoveryUint64Schema, acceptedAtUtc: fleetRecoveryUtcSchema,
}).strict();
export const fleetRecoveryHealthRequestSchema = z.object({ proof: deviceProofSchema, health: foundationHealthSchema }).strict()
  .refine(input => input.proof.deviceId === input.health.deviceId, 'Health proof device mismatch');
export const fleetRecoveryHealthAckSchema = z.object({ deviceId: fleetRecoveryDeviceIdSchema, acceptedAtUtc: fleetRecoveryUtcSchema }).strict();
export const fleetRecoveryInstallationRootsSchema = z.object({ generation: fleetRecoveryUint64Schema,
  bootId: fleetRecoveryIdSchema, observedAtUtc: fleetRecoveryUtcSchema, productRoot: fleetRecoveryPathSchema,
  dataRoot: fleetRecoveryPathSchema, evidenceSha256: fleetRecoverySha256Schema }).strict();
export const fleetRecoveryInstallationRootsRequestSchema = fleetRecoveryInstallationRootsSchema.extend({ proof: deviceProofSchema }).strict();
export const fleetRecoveryAuthorityProbeRequestSchema = z.object({ proof: deviceProofSchema,
  operationId: fleetRecoveryUuidSchema, generation: fleetRecoveryUint64Schema, transitionId: fleetRecoveryUuidSchema,
  transitionSha256: fleetRecoverySha256Schema, bootId: fleetRecoveryIdSchema, probeNonce: fleetRecoveryUuidSchema }).strict();
export const fleetRecoveryAuthorityProbeReplySchema = recoveryEnvelopeSchema(z.object({ schemaVersion: z.literal(2),
  purpose: z.literal('playcast-recovery-authority-probe'), deviceId: fleetRecoveryDeviceIdSchema,
  operationId: fleetRecoveryUuidSchema, generation: fleetRecoveryUint64Schema, transitionId: fleetRecoveryUuidSchema,
  transitionSha256: fleetRecoverySha256Schema, origin: z.string().url().max(2048), activeKeyId: fleetRecoveryIdSchema,
  bootId: fleetRecoveryIdSchema, probeNonce: fleetRecoveryUuidSchema, observedAtUtc: fleetRecoveryUtcSchema }).strict());
export const fleetRecoveryOperationSummarySchema = recoveryIdentitySchema.extend({ kind: recoveryKindSchema,
  packageSha256: fleetRecoverySha256Schema, requestedAtUtc: fleetRecoveryUtcSchema,
  deliveryState: z.enum(['queued', 'delivered', 'running', 'awaiting-reboot', 'blocked-recoverable', 'outcome-unknown']),
}).strict();
export const fleetRecoveryChannelSchema = z.object({ schemaVersion: z.literal(2),
  branch: z.string().min(1).max(128).regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/),
  publishedAtUtc: fleetRecoveryUtcSchema, manifestSha256: fleetRecoverySha256Schema,
  packageSha256: fleetRecoverySha256Schema, productVersion: z.string().min(1).max(128),
  minExecutorVersion: fleetRecoveryVersionSchema,
}).strict();
export const fleetRecoveryDeviceViewSchema = z.object({
  deviceId: fleetRecoveryDeviceIdSchema, health: foundationHealthSchema.nullable(), receipt: installationReceiptSchema.nullable(),
  activeOperation: fleetRecoveryOperationSummarySchema.nullable(),
  receivedAtUtc: fleetRecoveryUtcSchema.nullable(),
  recoverability: z.enum(['verified', 'unknown', 'bootstrap-required', 'bootstrap-unavailable', 'decommissioned']),
  bootstrapPath: fleetRecoveryIdSchema.nullable(),
}).strict().superRefine((view, context) => {
  if (view.health && view.health.deviceId !== view.deviceId || view.receipt && view.receipt.deviceId !== view.deviceId) {
    context.addIssue({ code: 'custom', message: 'Evidence device mismatch' });
  }
  if (view.activeOperation && view.activeOperation.deviceId !== view.deviceId) context.addIssue({ code: 'custom', message: 'Active operation targets different device' });
  if (view.recoverability === 'verified' && (!view.health || view.health.state !== 'ready' || !view.receivedAtUtc)) {
    context.addIssue({ code: 'custom', message: 'Verified recovery requires observed independent readiness' });
  }
});
export const fleetRecoveryAdminRequestSchema = z.object({
  kind: recoveryKindSchema, packageSha256: fleetRecoverySha256Schema, manifestSha256: fleetRecoverySha256Schema,
  idempotencyKey: fleetRecoveryUuidSchema, maxReboots: z.number().int().min(1).max(32), deadlineUtc: fleetRecoveryUtcSchema,
}).strict();
export const fleetRecoveryAcceptedSchema = z.object({ operationId: fleetRecoveryUuidSchema, deliveryState: z.literal('queued') }).strict();
